'use client';

import {
	AlertTriangle,
	BadgeCheck,
	CheckCircle2,
	Clock3,
	Eye,
	Megaphone,
	Pin,
	Plus,
	RefreshCw,
	ShieldCheck
} from 'lucide-react';
import type { ComponentType, FormEvent } from 'react';
import { useEffect, useMemo, useState } from 'react';

import { api, getErrorMessage } from '$lib/api/client';
import type {
	CreateNewsFeedAnnouncementRequest,
	NewsFeedAnnouncementCategory,
	NewsFeedAnnouncementResponse,
	NewsFeedAnnouncementStatus,
	NewsFeedChecklistItem,
	NewsFeedChecklistStatus,
	NewsFeedPlaybook,
	NewsFeedPriority,
	NewsFeedRiskItem,
	NewsFeedRiskSeverity,
	NewsFeedSummaryResponse
} from '$lib/api/types';
import {
	Badge,
	Button,
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
	EmptyState,
	Input,
	Label,
	PageHeader,
	Spinner
} from '$lib/ui';

type RequestState<T> =
	| { status: 'idle' | 'loading' }
	| { status: 'success'; data: T }
	| { status: 'error'; message: string };

type AnnouncementFormState = {
	category: NewsFeedAnnouncementCategory;
	priority: NewsFeedPriority;
	title: string;
	body: string;
	audienceLabel: string;
	pinned: boolean;
	requiresAcknowledgement: boolean;
	expiresAt: string;
};

const selectClassName =
	'border-input bg-background ring-offset-background focus-visible:ring-ring/35 h-10 w-full rounded-md border px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-offset-2';

function numberLabel(value: number | null | undefined): string {
	if (typeof value !== 'number' || !Number.isFinite(value)) return '0';
	return new Intl.NumberFormat('ko-KR').format(value);
}

function defaultExpiry(): string {
	const date = new Date();
	date.setDate(date.getDate() + 30);
	date.setHours(18, 0, 0, 0);
	return date.toISOString().slice(0, 16);
}

function dateTimeLabel(value: string | null | undefined): string {
	if (!value) return '-';
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return value;
	return new Intl.DateTimeFormat('ko-KR', {
		month: '2-digit',
		day: '2-digit',
		hour: '2-digit',
		minute: '2-digit'
	}).format(date);
}

function categoryLabel(category: NewsFeedAnnouncementCategory): string {
	const labels: Record<NewsFeedAnnouncementCategory, string> = {
		COMPANY_NOTICE: '전사 공지',
		POLICY: '정책',
		EVENT: '행사',
		PEOPLE: '구성원',
		SYSTEM: '시스템'
	};
	return labels[category] ?? category;
}

function priorityLabel(priority: NewsFeedPriority): string {
	const labels: Record<NewsFeedPriority, string> = {
		NORMAL: '일반',
		IMPORTANT: '중요',
		URGENT: '긴급'
	};
	return labels[priority] ?? priority;
}

function statusLabel(status: NewsFeedAnnouncementStatus): string {
	const labels: Record<NewsFeedAnnouncementStatus, string> = {
		DRAFT: '초안',
		PUBLISHED: '게시',
		ARCHIVED: '보관'
	};
	return labels[status] ?? status;
}

function checklistStatusLabel(status: NewsFeedChecklistStatus): string {
	if (status === 'READY') return '준비';
	if (status === 'WARNING') return '주의';
	return '대기';
}

function checklistVariant(status: NewsFeedChecklistStatus): 'success' | 'warning' | 'outline' {
	if (status === 'READY') return 'success';
	if (status === 'WARNING') return 'warning';
	return 'outline';
}

function riskVariant(severity: NewsFeedRiskSeverity): 'success' | 'warning' | 'outline' {
	if (severity === 'READY') return 'success';
	if (severity === 'WARNING') return 'warning';
	return 'outline';
}

function statusVariant(status: NewsFeedAnnouncementStatus): 'success' | 'warning' | 'outline' {
	if (status === 'PUBLISHED') return 'success';
	if (status === 'DRAFT') return 'warning';
	return 'outline';
}

function priorityVariant(priority: NewsFeedPriority): 'warning' | 'outline' | 'destructive' {
	if (priority === 'URGENT') return 'destructive';
	if (priority === 'IMPORTANT') return 'warning';
	return 'outline';
}

function deliveryMetricLabel(
	label: string,
	count: number | null | undefined,
	total: number | null | undefined,
	ratePercent: number | null | undefined
): string {
	return `${label} ${numberLabel(count)}/${numberLabel(total)}명 · ${numberLabel(ratePercent)}%`;
}

function StatCard({
	title,
	value,
	description,
	icon: Icon
}: {
	title: string;
	value: number | string;
	description: string;
	icon: ComponentType<{ className?: string }>;
}) {
	return (
		<Card>
			<CardContent className="flex min-h-32 flex-col justify-between p-4">
				<div className="flex items-center justify-between gap-3">
					<p className="text-xs font-medium text-muted-foreground">{title}</p>
					<div className="rounded-md bg-muted p-2 text-muted-foreground">
						<Icon className="size-4" />
					</div>
				</div>
				<div>
					<p className="text-2xl font-semibold tracking-normal">
						{typeof value === 'number' ? numberLabel(value) : value}
					</p>
					<p className="mt-1 text-xs text-muted-foreground">{description}</p>
				</div>
			</CardContent>
		</Card>
	);
}

function ChecklistGrid({ items }: { items: NewsFeedChecklistItem[] }) {
	if (items.length === 0) return <EmptyState message="공지 운영 체크리스트가 없습니다." />;
	return (
		<div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
			{items.map((item) => {
				const Icon = item.status === 'READY' ? CheckCircle2 : item.status === 'WARNING' ? AlertTriangle : Clock3;
				return (
					<div key={item.code} className="min-w-0 rounded-lg border border-border bg-background p-4">
						<div className="flex items-start justify-between gap-3">
							<div className="flex min-w-0 items-start gap-3">
								<div className="mt-0.5 rounded-md bg-muted p-2 text-muted-foreground">
									<Icon className="size-4" />
								</div>
								<div className="min-w-0">
									<p className="font-semibold">{item.label}</p>
									<p className="mt-1 text-sm text-muted-foreground">{item.description}</p>
								</div>
							</div>
							<Badge variant={checklistVariant(item.status)}>{checklistStatusLabel(item.status)}</Badge>
						</div>
					</div>
				);
			})}
		</div>
	);
}

function RiskPanel({ risks }: { risks: NewsFeedRiskItem[] }) {
	if (risks.length === 0) return <EmptyState message="표시할 소식 전파 리스크가 없습니다." />;
	return (
		<div className="min-w-0 rounded-lg border border-border bg-background p-4">
			<div className="flex items-center gap-2">
				<AlertTriangle className="size-4 text-muted-foreground" />
				<p className="font-semibold">전파 리스크</p>
			</div>
			<div className="mt-4 space-y-3">
				{risks.map((risk) => (
					<div key={risk.code} className="flex items-start justify-between gap-3">
						<div className="min-w-0">
							<p className="font-medium">{risk.title}</p>
							<p className="mt-1 text-sm text-muted-foreground">{risk.actionLabel}</p>
						</div>
						<Badge variant={riskVariant(risk.severity)}>{numberLabel(risk.count)}</Badge>
					</div>
				))}
			</div>
		</div>
	);
}

function AnnouncementFeed({
	announcements,
	actionId,
	onAcknowledge
}: {
	announcements: NewsFeedAnnouncementResponse[];
	actionId: number | null;
	onAcknowledge: (announcement: NewsFeedAnnouncementResponse) => void;
}) {
	if (announcements.length === 0) return <EmptyState message="게시된 사내 소식이 없습니다." />;
	return (
		<div className="space-y-3">
			{announcements.map((announcement) => {
				const canAcknowledge = announcement.status === 'PUBLISHED' && announcement.requiresAcknowledgement;
				const needsAcknowledgement = canAcknowledge && !announcement.acknowledgedAt;
				return (
					<div key={announcement.id} className="min-w-0 rounded-lg border border-border bg-background p-4">
						<div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
							<div className="min-w-0">
								<div className="flex flex-wrap items-center gap-2">
									{announcement.pinned ? (
										<Badge variant="outline">
											<Pin className="mr-1 size-3" />
											고정
										</Badge>
									) : null}
									<Badge variant={priorityVariant(announcement.priority)}>
										{priorityLabel(announcement.priority)}
									</Badge>
									<Badge variant={statusVariant(announcement.status)}>{statusLabel(announcement.status)}</Badge>
									<Badge variant="outline">{categoryLabel(announcement.category)}</Badge>
									{needsAcknowledgement ? <Badge variant="warning">확인 필요</Badge> : null}
								</div>
								<p className="mt-3 text-base font-semibold">{announcement.title}</p>
								<p className="mt-2 whitespace-pre-line text-sm text-muted-foreground">{announcement.body}</p>
								<div className="mt-3 flex flex-wrap gap-3 text-xs text-muted-foreground">
									<span>대상 {announcement.audienceLabel}</span>
									<span>작성 {announcement.authorName}</span>
									<span>게시 {dateTimeLabel(announcement.publishedAt)}</span>
									<span>만료 {dateTimeLabel(announcement.expiresAt)}</span>
								</div>
								<div className="mt-3 grid gap-2 text-xs text-muted-foreground sm:grid-cols-2">
									<div className="rounded-md bg-muted/40 px-3 py-2">
										<span>
											{deliveryMetricLabel(
												'읽음',
												announcement.readReceiptCount,
												announcement.recipientCount,
												announcement.readRatePercent
											)}
										</span>
									</div>
									<div className="rounded-md bg-muted/40 px-3 py-2">
										<span>
											{announcement.requiresAcknowledgement
												? deliveryMetricLabel(
														'확인',
														announcement.acknowledgementReceiptCount,
														announcement.recipientCount,
														announcement.acknowledgementRatePercent
													)
												: '확인 요청 없음'}
										</span>
									</div>
								</div>
							</div>
							<div className="flex shrink-0 flex-wrap gap-2">
								<Badge variant={announcement.readAt ? 'success' : 'outline'}>
									<Eye className="mr-1 size-3" />
									{announcement.readAt ? '읽음' : '미확인'}
								</Badge>
								{canAcknowledge ? (
									<Button
										type="button"
										size="sm"
										variant={announcement.acknowledgedAt ? 'outline' : 'default'}
										disabled={Boolean(announcement.acknowledgedAt) || actionId === announcement.id}
										onClick={() => onAcknowledge(announcement)}
									>
										<BadgeCheck className="size-4" />
										{announcement.acknowledgedAt ? '확인됨' : '확인 완료'}
									</Button>
								) : null}
							</div>
						</div>
					</div>
				);
			})}
		</div>
	);
}

function PlaybookGrid({ playbooks }: { playbooks: NewsFeedPlaybook[] }) {
	if (playbooks.length === 0) return <EmptyState message="공지 운영 플레이북이 없습니다." />;
	return (
		<div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
			{playbooks.map((playbook) => (
				<div key={playbook.code} className="min-w-0 rounded-lg border border-border bg-background p-4">
					<div className="rounded-md bg-muted p-2 text-muted-foreground">
						<ShieldCheck className="size-4" />
					</div>
					<p className="mt-3 font-semibold">{playbook.label}</p>
					<p className="mt-1 text-sm text-muted-foreground">{playbook.description}</p>
				</div>
			))}
		</div>
	);
}

function apiExpiresAt(value: string): string | null {
	return value ? `${value}:00` : null;
}

export function NewsFeedPage() {
	const [state, setState] = useState<RequestState<NewsFeedSummaryResponse>>({ status: 'idle' });
	const [actionMessage, setActionMessage] = useState<string | null>(null);
	const [operationLoading, setOperationLoading] = useState<string | null>(null);
	const [acknowledgingId, setAcknowledgingId] = useState<number | null>(null);
	const [form, setForm] = useState<AnnouncementFormState>({
		category: 'COMPANY_NOTICE',
		priority: 'IMPORTANT',
		title: '',
		body: '',
		audienceLabel: '전사',
		pinned: true,
		requiresAcknowledgement: true,
		expiresAt: defaultExpiry()
	});

	const load = useMemo(
		() => async () => {
			setState({ status: 'loading' });
			try {
				const summary = await api.get<NewsFeedSummaryResponse>('/news-feed/summary');
				setState({ status: 'success', data: summary });
			} catch (error) {
				setState({ status: 'error', message: getErrorMessage(error) });
			}
		},
		[]
	);

	useEffect(() => {
		let active = true;
		void Promise.resolve().then(async () => {
			if (active) await load();
		});
		return () => {
			active = false;
		};
	}, [load]);

	async function submitAnnouncement(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		setOperationLoading('announcement');
		setActionMessage(null);
		try {
			const request: CreateNewsFeedAnnouncementRequest = {
				category: form.category,
				priority: form.priority,
				title: form.title.trim(),
				body: form.body.trim(),
				audienceLabel: form.audienceLabel.trim(),
				pinned: form.pinned,
				requiresAcknowledgement: form.requiresAcknowledgement,
				expiresAt: apiExpiresAt(form.expiresAt)
			};
			const created = await api.post<NewsFeedAnnouncementResponse>('/news-feed/announcements', request);
			setState((current) => {
				if (current.status !== 'success') return current;
				return {
					status: 'success',
					data: {
						...current.data,
						totalAnnouncementCount: current.data.totalAnnouncementCount + 1,
						feed: [created, ...current.data.feed]
					}
				};
			});
			setForm((current) => ({ ...current, title: '', body: '', expiresAt: defaultExpiry() }));
			setActionMessage('공지 초안을 만들었습니다.');
		} catch (error) {
			setActionMessage(getErrorMessage(error));
		} finally {
			setOperationLoading(null);
		}
	}

	async function acknowledge(announcement: NewsFeedAnnouncementResponse) {
		setAcknowledgingId(announcement.id);
		setActionMessage(null);
		try {
			const updated = await api.patch<NewsFeedAnnouncementResponse>(
				`/news-feed/announcements/${announcement.id}/acknowledge`
			);
			setState((current) => {
				if (current.status !== 'success') return current;
				return {
					status: 'success',
					data: {
						...current.data,
						feed: current.data.feed.map((item) => (item.id === updated.id ? updated : item)),
						unreadAnnouncementCount: Math.max(0, current.data.unreadAnnouncementCount - 1),
						acknowledgementRequiredCount: Math.max(0, current.data.acknowledgementRequiredCount - 1)
					}
				};
			});
			setActionMessage('공지 확인 완료를 기록했습니다.');
		} catch (error) {
			setActionMessage(getErrorMessage(error));
		} finally {
			setAcknowledgingId(null);
		}
	}

	const data = state.status === 'success' ? state.data : null;

	return (
		<div className="min-w-0 space-y-6 overflow-x-hidden">
			<PageHeader
				title="사내 소식"
				description="전사 공지, 정책 안내, 컬처 이벤트를 게시하고 구성원별 읽음·확인 상태를 추적합니다."
				actions={
					<Button type="button" variant="outline" onClick={() => void load()}>
						<RefreshCw className="size-4" />
						새로고침
					</Button>
				}
			/>

			{state.status === 'loading' || state.status === 'idle' ? (
				<Card>
					<CardContent className="flex min-h-64 items-center justify-center">
						<Spinner size="lg" />
					</CardContent>
				</Card>
			) : null}

			{state.status === 'error' ? (
				<div className="rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
					{state.message}
				</div>
			) : null}

			{actionMessage ? (
				<div className="rounded-lg border border-border bg-muted/30 px-4 py-3 text-sm">{actionMessage}</div>
			) : null}

			{data ? (
				<>
					<div className="grid min-w-0 grid-cols-2 gap-3 lg:grid-cols-5">
						<StatCard
							title="게시 공지"
							value={data.totalAnnouncementCount}
							description="현재 피드에 노출"
							icon={Megaphone}
						/>
						<StatCard
							title="고정"
							value={data.pinnedAnnouncementCount}
							description="상단 노출 공지"
							icon={Pin}
						/>
						<StatCard
							title="미확인"
							value={data.unreadAnnouncementCount}
							description="읽음 기록 없음"
							icon={Eye}
						/>
						<StatCard
							title="확인 요청"
							value={data.acknowledgementRequiredCount}
							description="서명성 확인 필요"
							icon={BadgeCheck}
						/>
						<StatCard
							title="긴급"
							value={data.urgentAnnouncementCount}
							description="즉시 확인 필요"
							icon={AlertTriangle}
						/>
					</div>

					<div className="grid min-w-0 gap-4 xl:grid-cols-[minmax(0,0.82fr)_minmax(0,1.18fr)]">
						<Card className="min-w-0">
							<CardHeader>
								<CardTitle>공지 작성</CardTitle>
								<CardDescription>전사 공지, 정책 변경, 행사 안내를 초안으로 등록합니다.</CardDescription>
							</CardHeader>
							<CardContent>
								<form className="grid gap-3" onSubmit={submitAnnouncement}>
									<div className="grid gap-3 sm:grid-cols-2">
										<div>
											<Label htmlFor="news-category">유형</Label>
											<select
												id="news-category"
												className={selectClassName}
												value={form.category}
												onChange={(event) =>
													setForm((current) => ({
														...current,
														category: event.target.value as NewsFeedAnnouncementCategory
													}))
												}
											>
												<option value="COMPANY_NOTICE">전사 공지</option>
												<option value="POLICY">정책</option>
												<option value="EVENT">행사</option>
												<option value="PEOPLE">구성원</option>
												<option value="SYSTEM">시스템</option>
											</select>
										</div>
										<div>
											<Label htmlFor="news-priority">우선순위</Label>
											<select
												id="news-priority"
												className={selectClassName}
												value={form.priority}
												onChange={(event) =>
													setForm((current) => ({
														...current,
														priority: event.target.value as NewsFeedPriority
													}))
												}
											>
												<option value="NORMAL">일반</option>
												<option value="IMPORTANT">중요</option>
												<option value="URGENT">긴급</option>
											</select>
										</div>
									</div>
									<div>
										<Label htmlFor="news-title">제목</Label>
										<Input
											id="news-title"
											value={form.title}
											onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))}
											placeholder="예: 전사 보안 점검 안내"
											required
										/>
									</div>
									<div>
										<Label htmlFor="news-body">본문</Label>
										<textarea
											id="news-body"
											className="border-input bg-background ring-offset-background focus-visible:ring-ring/35 min-h-28 w-full rounded-md border px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
											value={form.body}
											onChange={(event) => setForm((current) => ({ ...current, body: event.target.value }))}
											placeholder="구성원이 바로 이해할 수 있도록 변경 사항과 필요한 행동을 적습니다."
											required
										/>
									</div>
									<div className="grid gap-3 sm:grid-cols-2">
										<div>
											<Label htmlFor="news-audience">대상</Label>
											<Input
												id="news-audience"
												value={form.audienceLabel}
												onChange={(event) =>
													setForm((current) => ({ ...current, audienceLabel: event.target.value }))
												}
												required
											/>
										</div>
										<div>
											<Label htmlFor="news-expires">만료 시각</Label>
											<Input
												id="news-expires"
												type="datetime-local"
												value={form.expiresAt}
												onChange={(event) => setForm((current) => ({ ...current, expiresAt: event.target.value }))}
											/>
										</div>
									</div>
									<div className="grid gap-2 sm:grid-cols-2">
										<label className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm">
											<input
												type="checkbox"
												checked={form.pinned}
												onChange={(event) =>
													setForm((current) => ({ ...current, pinned: event.target.checked }))
												}
											/>
											상단 고정
										</label>
										<label className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm">
											<input
												type="checkbox"
												checked={form.requiresAcknowledgement}
												onChange={(event) =>
													setForm((current) => ({
														...current,
														requiresAcknowledgement: event.target.checked
													}))
												}
											/>
											확인 완료 요청
										</label>
									</div>
									<Button type="submit" disabled={operationLoading === 'announcement'}>
										<Plus className="size-4" />
										공지 초안 만들기
									</Button>
								</form>
							</CardContent>
						</Card>

						<Card className="min-w-0">
							<CardHeader>
								<CardTitle>소식 피드</CardTitle>
								<CardDescription>고정 공지, 긴급 안내, 읽음 상태와 확인 완료를 한 화면에서 처리합니다.</CardDescription>
							</CardHeader>
							<CardContent>
								<AnnouncementFeed
									announcements={data.feed}
									actionId={acknowledgingId}
									onAcknowledge={(announcement) => void acknowledge(announcement)}
								/>
							</CardContent>
						</Card>
					</div>

					<div className="grid min-w-0 gap-4 xl:grid-cols-[minmax(0,1.25fr)_minmax(0,0.75fr)]">
						<Card className="min-w-0">
							<CardHeader>
								<CardTitle>운영 체크리스트</CardTitle>
								<CardDescription>공지 라이브러리, 고정 공지, 읽음 추적, 확인 완료 루프를 점검합니다.</CardDescription>
							</CardHeader>
							<CardContent>
								<ChecklistGrid items={data.checklist} />
							</CardContent>
						</Card>
						<RiskPanel risks={data.riskItems} />
					</div>

					<Card className="min-w-0">
						<CardHeader>
							<CardTitle>소식 운영 플레이북</CardTitle>
							<CardDescription>전사 공지, 정책 배포, 확인 캠페인, 컬처 이벤트 운영 루틴을 실행합니다.</CardDescription>
						</CardHeader>
						<CardContent>
							<PlaybookGrid playbooks={data.playbooks} />
						</CardContent>
					</Card>
				</>
			) : null}
		</div>
	);
}
